/**
 * Standard MIDI File (SMF) parser — Format 0 & 1, client-side only.
 * Extracts tempo, time signature, and note events from all tracks.
 */

export interface RawMidiNote {
  /** Absolute tick from start. */
  tick: number;
  durationTicks: number;
  midi: number;
  velocity: number;
  channel: number;
  trackIndex: number;
  trackName: string;
}

export interface ParsedMidiFile {
  format: 0 | 1 | 2;
  ticksPerQuarter: number;
  trackCount: number;
  /** µs per quarter note (from first/last Set Tempo; default 500_000 = 120 BPM). */
  tempoUsq: number;
  numerator: number;
  denominator: number;
  notes: RawMidiNote[];
  trackNames: string[];
}

function readStr(view: DataView, offset: number, len: number): string {
  let s = "";
  for (let i = 0; i < len; i++) s += String.fromCharCode(view.getUint8(offset + i));
  return s;
}

function readVarLen(view: DataView, offset: number): { value: number; size: number } {
  let value = 0;
  let size = 0;
  while (size < 4) {
    const b = view.getUint8(offset + size);
    size += 1;
    value = (value << 7) | (b & 0x7f);
    if ((b & 0x80) === 0) break;
  }
  return { value, size };
}

interface OpenNote {
  tick: number;
  velocity: number;
  trackIndex: number;
  trackName: string;
}

/**
 * Parse a Standard MIDI File from an ArrayBuffer / Uint8Array.
 */
export function parseMidiFile(input: ArrayBuffer | Uint8Array): ParsedMidiFile {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  if (bytes.length < 14 || readStr(view, 0, 4) !== "MThd") {
    throw new Error("Not a valid MIDI file (missing MThd)");
  }

  const headerLen = view.getUint32(4);
  const format = view.getUint16(8) as 0 | 1 | 2;
  const trackCount = view.getUint16(10);
  const division = view.getUint16(12);

  if (division & 0x8000) {
    throw new Error("SMPTE time-code MIDI files are not supported");
  }
  const ticksPerQuarter = division;

  let offset = 8 + headerLen;
  let tempoUsq = 500_000;
  let numerator = 4;
  let denominator = 4;
  const trackNames: string[] = [];
  const notes: RawMidiNote[] = [];

  for (let t = 0; t < trackCount; t++) {
    if (offset + 8 > bytes.length) break;
    const chunkId = readStr(view, offset, 4);
    const chunkLen = view.getUint32(offset + 4);
    offset += 8;
    if (chunkId !== "MTrk") {
      offset += chunkLen;
      continue;
    }

    const trackEnd = offset + chunkLen;
    let tick = 0;
    let runningStatus = 0;
    let trackName = `Track ${t + 1}`;
    const open = new Map<string, OpenNote>(); // channel:note → open

    while (offset < trackEnd) {
      const delta = readVarLen(view, offset);
      offset += delta.size;
      tick += delta.value;

      if (offset >= trackEnd) break;
      let status = view.getUint8(offset);

      if (status < 0x80) {
        // running status
        status = runningStatus;
      } else {
        offset += 1;
        if (status < 0xf0) runningStatus = status;
      }

      const high = status & 0xf0;
      const channel = status & 0x0f;

      if (status === 0xff) {
        // Meta
        const metaType = view.getUint8(offset);
        offset += 1;
        const len = readVarLen(view, offset);
        offset += len.size;
        const dataStart = offset;
        offset += len.value;

        if (metaType === 0x51 && len.value === 3) {
          tempoUsq =
            (view.getUint8(dataStart) << 16) |
            (view.getUint8(dataStart + 1) << 8) |
            view.getUint8(dataStart + 2);
        } else if (metaType === 0x58 && len.value >= 2) {
          numerator = view.getUint8(dataStart);
          denominator = 2 ** view.getUint8(dataStart + 1);
        } else if (metaType === 0x03) {
          let name = "";
          for (let i = 0; i < len.value; i++) {
            name += String.fromCharCode(view.getUint8(dataStart + i));
          }
          if (name.trim()) trackName = name.trim();
        }
        continue;
      }

      if (status === 0xf0 || status === 0xf7) {
        const len = readVarLen(view, offset);
        offset += len.size + len.value;
        continue;
      }

      if (high === 0x80 || high === 0x90) {
        const note = view.getUint8(offset);
        const velocity = view.getUint8(offset + 1);
        offset += 2;
        const key = `${channel}:${note}`;
        const isOn = high === 0x90 && velocity > 0;

        if (isOn) {
          open.set(key, {
            tick,
            velocity,
            trackIndex: t,
            trackName,
          });
        } else {
          const started = open.get(key);
          if (started) {
            notes.push({
              tick: started.tick,
              durationTicks: Math.max(1, tick - started.tick),
              midi: note,
              velocity: started.velocity,
              channel,
              trackIndex: started.trackIndex,
              trackName: started.trackName,
            });
            open.delete(key);
          }
        }
        continue;
      }

      if (high === 0xa0 || high === 0xb0 || high === 0xe0) {
        offset += 2;
        continue;
      }
      if (high === 0xc0 || high === 0xd0) {
        offset += 1;
        continue;
      }

      // Unknown / system — best-effort skip
      break;
    }

    // Close hanging notes at track end
    for (const [key, started] of open) {
      const note = Number(key.split(":")[1]);
      notes.push({
        tick: started.tick,
        durationTicks: Math.max(1, tick - started.tick),
        midi: note,
        velocity: started.velocity,
        channel: Number(key.split(":")[0]),
        trackIndex: started.trackIndex,
        trackName: started.trackName,
      });
    }

    trackNames.push(trackName);
    offset = trackEnd;
  }

  notes.sort((a, b) => a.tick - b.tick || a.midi - b.midi);

  return {
    format,
    ticksPerQuarter,
    trackCount,
    tempoUsq,
    numerator,
    denominator,
    notes,
    trackNames,
  };
}

export function parsedBpm(parsed: ParsedMidiFile): number {
  return Math.round(60_000_000 / parsed.tempoUsq);
}
